import {
	ColorPalette,
	ColorPaletteOptions,
	ColorPaletteFunctions,
	ColorPaletteRoles,
	ColorPaletteRoleOptions,
	ColorPaletteMandatoryOptions,
	ColorPaletteSupportingOptions,
} from "../types/theming";

export class Palette implements ColorPaletteFunctions {
	private _palette: ColorPalette;
	private _roles: ColorPaletteRoles;

	constructor(palette: ColorPalette, roles: ColorPaletteRoles) {
		this._palette = palette;
		this._roles = roles;
	}

	/**
	 * Call to resolve a token. It is advisable to call this function when resolving a token
	 * to a hex value, so we get visibility of a console log entry if an invalid token is passed.
	 * @param token A token to resolve.
	 * @returns A hex value if the passed token is valid, otherwise undefined.
	 */
	hexFor(token: ColorPaletteOptions | undefined): string | undefined {
		if (!token) return;
		const paletteKey = this._roles[token as ColorPaletteRoleOptions] || token;
		const HEX = this._palette[paletteKey];
		if (!HEX)
			console.error(
				`Error in hexFor function: no palette mapping for token ${token}`
			);
		return HEX;
	}

	/**
	 * Call to resolve a palette's role token, which returns a hex value.
	 * @param token A role token to resolve.
	 * @returns A hex value if the passed token is valid, otherwise undefined.
	 */
	hexForRole(token: ColorPaletteRoleOptions | undefined): string | undefined {
		return this.hexFor(token);
	}

	/**
	 * Call to resolve a palette's mandatory token, which returns a hex value.
	 * @param token A role token to resolve.
	 * @returns A hex value if the passed token is valid, otherwise undefined.
	 */
	hexForMandatory(
		token: ColorPaletteMandatoryOptions | undefined
	): string | undefined {
		return this.hexFor(token);
	}

	/**
	 * Call to resolve a palette's supporting token, which returns a hex value.
	 * @param token A role token to resolve.
	 * @returns A hex value if the passed token is valid, otherwise undefined.
	 */
	hexForSupporting(
		token: ColorPaletteSupportingOptions | undefined
	): string | undefined {
		return this.hexFor(token);
	}
}

/**
 * Call to create a new Palette instance.
 * @param palette A brand's color palette.
 * @param roles A roles' palette.
 * @returns A new Palette instance.
 */
export function createPalette(
	palette: ColorPalette,
	roles: ColorPaletteRoles
): Palette {
	return new Palette(palette, roles);
}
